import { Shield, Eye, Server, Cookie, UserCheck, Mail, FileText } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSeo } from "@/hooks/useSeo";

interface DatenschutzSeiteProps {
  onNavigate: (path: string) => void;
}

export function DatenschutzSeite({ onNavigate }: DatenschutzSeiteProps) {
  useSeo({
    title: "Datenschutzerklärung - zelt.kaufen",
    description: "Datenschutzerklärung von zelt.kaufen gemäß DSGVO. Informationen zur Erhebung und Verarbeitung personenbezogener Daten.",
    type: "website",
  });

  return (
    <div className="min-h-screen py-12">
      <Container className="max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Datenschutzerklärung</h1>
          <p className="text-lg text-muted-foreground">
            Informationen gemäß DSGVO
          </p>
        </div>

        <div className="space-y-8">
          {/* Verantwortlicher */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-primary" />
                1. Verantwortlicher
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-muted-foreground leading-relaxed">
              <p>Verantwortlicher im Sinne der DSGVO ist:</p>
              <p className="font-semibold text-foreground">Pykez UG (haftungsbeschränkt)</p>
              <p>Danziger Str. 4a<br />36251 Bad Hersfeld<br />Deutschland</p>
              <p>E-Mail: <a href="mailto:mail@pykez.com" className="text-primary hover:underline">mail@pykez.com</a></p>
            </CardContent>
          </Card>

          {/* Datenerhebung */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-primary" />
                2. Erhebung und Speicherung personenbezogener Daten
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <div>
                <h3 className="font-semibold text-foreground mb-2">a) Beim Besuch der Website</h3>
                <p>
                  Beim Aufrufen unserer Website werden durch den Browser automatisch Informationen an den
                  Server übermittelt. Diese Informationen werden temporär in einem sogenannten Logfile
                  gespeichert. Folgende Informationen werden dabei ohne Ihr Zutun erfasst und bis zur
                  automatisierten Löschung gespeichert:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>IP-Adresse des anfragenden Rechners</li>
                  <li>Datum und Uhrzeit des Zugriffs</li>
                  <li>Name und URL der abgerufenen Datei</li>
                  <li>Website, von der aus der Zugriff erfolgt (Referrer-URL)</li>
                  <li>Verwendeter Browser und ggf. das Betriebssystem Ihres Rechners</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">b) Bei Kontaktaufnahme</h3>
                <p>
                  Bei Fragen jeglicher Art bieten wir Ihnen die Möglichkeit, mit uns per E-Mail Kontakt
                  aufzunehmen. Dabei ist die Angabe einer gültigen E-Mail-Adresse erforderlich, damit wir
                  wissen, von wem die Anfrage stammt und um diese beantworten zu können. Die Verarbeitung
                  erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cookie className="h-5 w-5 text-primary" />
                3. Cookies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Wir setzen auf unserer Seite technisch notwendige Cookies ein. Hierbei handelt es sich um
                kleine Dateien, die Ihr Browser automatisch erstellt und die auf Ihrem Endgerät gespeichert
                werden. Cookies richten auf Ihrem Endgerät keinen Schaden an und enthalten keine Viren.
              </p>
              <p>
                Die von uns verwendeten Cookies dienen dazu, die Nutzung unserer Website zu ermöglichen
                (z.B. Speicherung des Dark-Mode-Einstellung, Vergleichsliste). Diese technisch notwendigen
                Cookies werden auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO gespeichert.
              </p>
              <p>
                Sie können Ihren Browser so einstellen, dass Sie über das Setzen von Cookies informiert
                werden und Cookies nur im Einzelfall erlauben. Bei der Deaktivierung von Cookies kann die
                Funktionalität unserer Website eingeschränkt sein.
              </p>
            </CardContent>
          </Card>

          {/* Affiliate */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                4. Affiliate-Links
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Unsere Website enthält sogenannte Affiliate-Links zu Online-Shops und Partnerprogrammen.
                Wenn Sie auf einen solchen Link klicken und auf der Zielseite einkaufen, erhalten wir eine
                Provision. Die Nutzung von Affiliate-Links erfolgt auf Grundlage unseres berechtigten
                Interesses an der wirtschaftlichen Finanzierung unseres Webangebots gemäß Art. 6 Abs. 1
                lit. f DSGVO.
              </p>
              <p>
                Beim Klick auf einen Affiliate-Link wird ggf. ein Cookie gesetzt, um die Zuordnung des
                Kaufs zu ermöglichen. Personenbezogene Daten werden von uns in diesem Zusammenhang nicht
                erhoben.
              </p>
            </CardContent>
          </Card>

          {/* Hosting */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5 text-primary" />
                5. Hosting
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground leading-relaxed">
              <p>
                Diese Website wird bei einem externen Dienstleister gehostet (Hoster). Die
                personenbezogenen Daten, die auf dieser Website erfasst werden, werden auf den Servern des
                Hosters gespeichert. Hierbei kann es sich v. a. um IP-Adressen, Kontaktanfragen, Meta- und
                Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Websitezugriffe und sonstige Daten,
                die über eine Website generiert werden, handeln. Die Verarbeitung erfolgt auf Grundlage von
                Art. 6 Abs. 1 lit. f DSGVO.
              </p>
            </CardContent>
          </Card>

          {/* Rechte */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                6. Ihre Rechte
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Sie haben gegenüber uns folgende Rechte hinsichtlich der Sie betreffenden personenbezogenen Daten:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong className="text-foreground">Recht auf Auskunft</strong> (Art. 15 DSGVO)</li>
                <li><strong className="text-foreground">Recht auf Berichtigung</strong> (Art. 16 DSGVO)</li>
                <li><strong className="text-foreground">Recht auf Löschung</strong> (Art. 17 DSGVO)</li>
                <li><strong className="text-foreground">Recht auf Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO)</li>
                <li><strong className="text-foreground">Recht auf Datenübertragbarkeit</strong> (Art. 20 DSGVO)</li>
                <li><strong className="text-foreground">Widerspruchsrecht</strong> (Art. 21 DSGVO)</li>
              </ul>
              <p>
                Sie haben zudem das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die Verarbeitung
                Ihrer personenbezogenen Daten durch uns zu beschweren.
              </p>
            </CardContent>
          </Card>

          {/* Änderungen */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                7. Aktualität und Änderung dieser Datenschutzerklärung
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground leading-relaxed">
              <p>
                Diese Datenschutzerklärung ist aktuell gültig und hat den Stand Februar 2026. Durch die
                Weiterentwicklung unserer Website oder aufgrund geänderter gesetzlicher bzw. behördlicher
                Vorgaben kann es notwendig werden, diese Datenschutzerklärung zu ändern.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Back Link */}
        <div className="mt-12 text-center">
          <button
            onClick={() => onNavigate("/")}
            className="text-primary hover:underline"
          >
            ← Zurück zur Startseite
          </button>
        </div>
      </Container>
    </div>
  );
}
